import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Filter, Download, RefreshCw, LogOut, 
  ChevronDown, ChevronUp, ExternalLink, Mail, Phone,
  MapPin, FileText, Clock, Users, CheckCircle, AlertCircle,
  XCircle, Loader2, Lock, Eye, EyeOff, Settings, Key
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const APPLICATION_STATUSES = [
  "Pending",
  "In Review",
  "Site Survey",
  "LOI Sent",
  "Installation Scheduled",
  "Completed",
  "Rejected"
];

const STATUS_COLORS = {
  "Pending": "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  "In Review": "bg-blue-500/20 text-blue-400 border-blue-500/30",
  "Site Survey": "bg-purple-500/20 text-purple-400 border-purple-500/30",
  "LOI Sent": "bg-orange-500/20 text-orange-400 border-orange-500/30",
  "Installation Scheduled": "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
  "Completed": "bg-green-500/20 text-green-400 border-green-500/30",
  "Rejected": "bg-red-500/20 text-red-400 border-red-500/30"
};

const Admin = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Login form
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Change password modal
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [updatingRow, setUpdatingRow] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/auth/me`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }
        
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
        setLoading(false);
      } catch (err) {
        console.error('Auth check failed:', err);
        setIsAuthenticated(false);
        setLoading(false);
      }
    };

    checkAuth();
  }, [backendUrl]);

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${backendUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password })
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Login failed');
      }
      
      const userData = await response.json();
      setUser(userData);
      setIsAuthenticated(true);
      toast.success('Login successful!');
      
      // Prompt to change password if using default
      if (!userData.password_changed) {
        toast.info('Please change your default password for security', { duration: 5000 });
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await fetch(`${backendUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (err) {
      console.error('Logout error:', err);
    }
    setUser(null);
    setIsAuthenticated(false);
    setApplications([]);
    setStats(null);
    toast.success('Logged out successfully');
  };

  // Handle password change
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangePasswordLoading(true);
    
    try {
      const response = await fetch(`${backendUrl}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          current_password: currentPassword,
          new_password: newPassword 
        })
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to change password');
      }
      
      toast.success('Password changed successfully!');
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setChangePasswordLoading(false);
    }
  };

  // Fetch applications
  const fetchApplications = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await fetch(
        `${backendUrl}/api/admin/applications?${params.toString()}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch applications');
      }
      
      const data = await response.json();
      setApplications(data.applications);
    } catch (err) {
      console.error('Fetch error:', err);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [backendUrl, isAuthenticated, statusFilter, searchTerm]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/stats`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Stats fetch error:', err);
    }
  }, [backendUrl, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchApplications();
      fetchStats();
    }
  }, [isAuthenticated, fetchApplications, fetchStats]);

  // Update application status
  const handleStatusUpdate = async (rowNumber, newStatus) => {
    setUpdatingRow(rowNumber);
    try {
      const response = await fetch(`${backendUrl}/api/admin/applications/${rowNumber}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update status');
      }
      
      const result = await response.json();
      
      let notifications = [];
      if (result.email_sent) notifications.push('Email');
      if (result.sms_sent) notifications.push('SMS');
      
      const notifMsg = notifications.length > 0 
        ? ` - ${notifications.join(' & ')} sent to applicant` 
        : '';
      
      toast.success(`Status updated to "${newStatus}"${notifMsg}`);
      fetchApplications();
    } catch (err) {
      console.error('Update error:', err);
      toast.error('Failed to update status');
    } finally {
      setUpdatingRow(null);
    }
  };

  // Update notes
  const handleNotesUpdate = async (rowNumber, notes) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/applications/${rowNumber}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notes })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update notes');
      }
      
      toast.success('Notes updated');
      fetchApplications();
    } catch (err) {
      console.error('Notes update error:', err);
      toast.error('Failed to update notes');
    }
  };

  // Export CSV
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await fetch(
        `${backendUrl}/api/admin/applications/export?${params.toString()}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fiberstream_applications_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success('CSV exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export CSV');
    }
  };

  // Loading state
  if (loading && isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-cyan-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Admin Dashboard</h1>
            <p className="text-gray-400">FiberStream Application Management</p>
          </div>
          
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                className="bg-slate-800 border-slate-700 text-white"
                required
                data-testid="username-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="bg-slate-800 border-slate-700 text-white pr-10"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <Button 
              type="submit"
              disabled={loginLoading}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-white py-6 text-lg rounded-xl"
              data-testid="login-btn"
            >
              {loginLoading ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={20} />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
          
          <p className="mt-6 text-xs text-gray-500 text-center">
            Default credentials: admin / hvgtec2024
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_hvgtec-home/artifacts/8p3d2ihe_HVGTEC%20AI%20Logo.png" 
                alt="HVGTEC" 
                className="h-10 w-10"
              />
              <div>
                <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
                <p className="text-sm text-gray-400">FiberStream Applications</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm text-white">{user?.name}</p>
                <p className="text-xs text-gray-400">@{user?.username}</p>
              </div>
              <Button 
                onClick={() => setShowChangePassword(true)} 
                variant="ghost" 
                size="icon"
                className="text-gray-400 hover:text-white"
                title="Change Password"
              >
                <Key size={20} />
              </Button>
              <Button 
                onClick={handleLogout} 
                variant="ghost" 
                size="icon"
                className="text-gray-400 hover:text-white"
                data-testid="logout-btn"
                title="Logout"
              >
                <LogOut size={20} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Key className="text-cyan-400" />
              Change Password
            </h2>
            
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Current Password</label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">New Password</label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                  minLength={6}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Confirm New Password</label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowChangePassword(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="flex-1 border-slate-600 text-gray-300"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  disabled={changePasswordLoading}
                  className="flex-1 bg-cyan-500 hover:bg-cyan-600"
                >
                  {changePasswordLoading ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    'Update Password'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {APPLICATION_STATUSES.map(status => (
              <div 
                key={status}
                className={`bg-slate-900 border rounded-xl p-4 cursor-pointer transition-all hover:scale-105 ${
                  statusFilter === status ? 'border-cyan-500' : 'border-slate-700'
                }`}
                onClick={() => setStatusFilter(statusFilter === status ? '' : status)}
                data-testid={`filter-${status.toLowerCase().replace(' ', '-')}`}
              >
                <div className="text-2xl font-bold text-white">
                  {stats.status_counts[status] || 0}
                </div>
                <div className="text-xs text-gray-400 truncate">{status}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters & Actions */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex flex-1 gap-4 w-full sm:w-auto">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                placeholder="Search by name, email, phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-900 border-slate-700 text-white"
                data-testid="search-input"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-slate-900 border border-slate-700 text-white rounded-lg px-4 py-2"
              data-testid="status-filter"
            >
              <option value="">All Statuses</option>
              {APPLICATION_STATUSES.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          
          <div className="flex gap-2">
            <Button 
              onClick={fetchApplications} 
              variant="outline" 
              className="border-slate-700 text-gray-300"
              data-testid="refresh-btn"
            >
              <RefreshCw size={18} className="mr-2" />
              Refresh
            </Button>
            <Button 
              onClick={handleExport}
              className="bg-cyan-500 hover:bg-cyan-600"
              data-testid="export-btn"
            >
              <Download size={18} className="mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
      </div>

      {/* Applications Table */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-400 mx-auto mb-2" />
              <p className="text-gray-400">Loading applications...</p>
            </div>
          ) : applications.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No applications found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="applications-table">
                <thead className="bg-slate-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Applicant</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Plan</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {applications.map((app) => (
                    <React.Fragment key={app.row_number}>
                      <tr 
                        className="hover:bg-slate-800/50 transition-colors cursor-pointer"
                        onClick={() => setExpandedRow(expandedRow === app.row_number ? null : app.row_number)}
                        data-testid={`app-row-${app.row_number}`}
                      >
                        <td className="px-4 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-cyan-500/20 rounded-full flex items-center justify-center">
                              <span className="text-cyan-400 font-semibold">
                                {app.name?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            </div>
                            <div>
                              <p className="text-white font-medium">{app.name}</p>
                              <p className="text-gray-400 text-sm">{app.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-white">{app.plan || 'N/A'}</span>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-white">{app.barangay}</p>
                          <p className="text-gray-400 text-sm">{app.municipality}</p>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-gray-300 text-sm">{app.timestamp}</p>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[app.status] || STATUS_COLORS.Pending}`}>
                            {app.status}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <Button variant="ghost" size="icon" className="text-gray-400">
                            {expandedRow === app.row_number ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                          </Button>
                        </td>
                      </tr>
                      
                      {/* Expanded Details */}
                      {expandedRow === app.row_number && (
                        <tr className="bg-slate-800/30">
                          <td colSpan={6} className="px-4 py-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                              {/* Contact Info */}
                              <div className="space-y-3">
                                <h4 className="text-white font-semibold flex items-center">
                                  <Users size={16} className="mr-2 text-cyan-400" />
                                  Contact Information
                                </h4>
                                <div className="space-y-2 text-sm">
                                  <p className="flex items-center text-gray-300">
                                    <Mail size={14} className="mr-2 text-gray-500" />
                                    <a href={`mailto:${app.email}`} className="hover:text-cyan-400">{app.email}</a>
                                  </p>
                                  <p className="flex items-center text-gray-300">
                                    <Phone size={14} className="mr-2 text-gray-500" />
                                    <a href={`tel:${app.phone}`} className="hover:text-cyan-400">{app.phone}</a>
                                  </p>
                                </div>
                              </div>
                              
                              {/* Location */}
                              <div className="space-y-3">
                                <h4 className="text-white font-semibold flex items-center">
                                  <MapPin size={16} className="mr-2 text-cyan-400" />
                                  Location Details
                                </h4>
                                <div className="space-y-2 text-sm">
                                  <p className="text-gray-300">{app.address}</p>
                                  <p className="text-gray-400">{app.barangay}, {app.municipality}</p>
                                  {app.location_link && (
                                    <a 
                                      href={app.location_link} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center text-cyan-400 hover:text-cyan-300"
                                    >
                                      <ExternalLink size={14} className="mr-1" />
                                      View on Map
                                    </a>
                                  )}
                                </div>
                              </div>
                              
                              {/* ID Document */}
                              <div className="space-y-3">
                                <h4 className="text-white font-semibold flex items-center">
                                  <FileText size={16} className="mr-2 text-cyan-400" />
                                  Documents
                                </h4>
                                <p className="text-sm text-gray-300">
                                  ID: {app.id_document || 'Not uploaded'}
                                </p>
                              </div>
                              
                              {/* Status Update */}
                              <div className="space-y-3">
                                <h4 className="text-white font-semibold flex items-center">
                                  <CheckCircle size={16} className="mr-2 text-cyan-400" />
                                  Update Status
                                </h4>
                                <select
                                  value={app.status}
                                  onChange={(e) => handleStatusUpdate(app.row_number, e.target.value)}
                                  disabled={updatingRow === app.row_number}
                                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm"
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`status-select-${app.row_number}`}
                                >
                                  {APPLICATION_STATUSES.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                  ))}
                                </select>
                                {updatingRow === app.row_number && (
                                  <p className="text-xs text-cyan-400 flex items-center">
                                    <Loader2 size={12} className="animate-spin mr-1" />
                                    Updating...
                                  </p>
                                )}
                              </div>
                              
                              {/* Notes */}
                              <div className="space-y-3 md:col-span-2">
                                <h4 className="text-white font-semibold flex items-center">
                                  <FileText size={16} className="mr-2 text-cyan-400" />
                                  Notes
                                </h4>
                                <textarea
                                  defaultValue={app.notes}
                                  placeholder="Add notes about this application..."
                                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm min-h-[80px]"
                                  onClick={(e) => e.stopPropagation()}
                                  onBlur={(e) => {
                                    if (e.target.value !== app.notes) {
                                      handleNotesUpdate(app.row_number, e.target.value);
                                    }
                                  }}
                                  data-testid={`notes-${app.row_number}`}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Total Count */}
          <div className="px-4 py-3 bg-slate-800/50 border-t border-slate-800">
            <p className="text-sm text-gray-400">
              Showing {applications.length} application{applications.length !== 1 ? 's' : ''}
              {statusFilter && ` with status "${statusFilter}"`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
